import { useMemo } from 'react'

import { useAuthStore } from '@/stores/auth'

import { paths } from '@/config/paths'

import { useHasActiveMembership, useHasViewableMembership, useIsMember } from '@/hooks/useMembership'

import type { NavItem } from '@/types/nav-item'

/**
 * Recursively filter navigation items by URL
 * @param items Navigation items to filter
 * @param predicate Function that returns true if item should be included
 * @returns Filtered navigation items
 */
function filterNavItems(items: NavItem[], predicate: (url: string) => boolean): NavItem[] {
  return items
    .map((item) => {
      // Check if current item should be included
      const includeItem = predicate(item.url)

      // Handle nested items recursively
      if (item.items && item.items.length > 0) {
        const filteredSubItems = filterNavItems(item.items, predicate)

        // If parent is blocked but has allowed children, keep parent with filtered children
        if (filteredSubItems.length > 0) {
          return {
            ...item,
            items: filteredSubItems,
          }
        }

        // If parent is allowed but has no children after filtering, keep parent without children
        if (includeItem) {
          return {
            ...item,
            items: [],
          }
        }

        // Parent blocked and no children - exclude entirely
        return null
      }

      // Leaf item - return based on predicate
      return includeItem ? item : null
    })
    .filter((item): item is NavItem => item !== null)
}

/**
 * Hook to filter navigation items based on user's membership status.
 *
 * Three tiers of access:
 * 1. Active membership: Full access (no filtering)
 * 2. Paused membership: View-only access (payments, notes, health tracking, but no bookings)
 * 3. No membership: Minimal access (dashboard, membership selection, profile)
 */
export function useFilteredNav(navItems: NavItem[]): NavItem[] {
  const user = useAuthStore((state) => state.user)
  const isMember = useIsMember()
  const hasActiveMembership = useHasActiveMembership()
  const hasViewableMembership = useHasViewableMembership()

  return useMemo(() => {
    if (!user) return navItems

    // Non-members or active members see everything
    if (!isMember || hasActiveMembership) return navItems

    // Members with paused membership: hide only booking-related items
    if (hasViewableMembership) {
      const blockedUrls = new Set([
        paths.dashboard.member.bookClass.getHref(),
        paths.dashboard.member.myBookings.getHref(),
      ])

      return filterNavItems(navItems, (url) => !blockedUrls.has(url))
    }

    // Members without any active/paused membership: minimal access
    const allowedUrls = new Set([
      paths.dashboard.root.getHref(),
      paths.dashboard.member.myMembership.getHref(),
      paths.dashboard.profile.root.getHref(),
    ])

    return filterNavItems(navItems, (url) => allowedUrls.has(url))
  }, [navItems, user, isMember, hasActiveMembership, hasViewableMembership])
}
